import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { ChevronLeftIcon, ChevronRightIcon } from '../shell/icons';

import type { PaginationState } from './types';

interface Props {
  pagination: PaginationState;
  pageSizeOptions?: number[];
}

const DEFAULT_SIZES = [10, 25, 50, 100];

export function Pagination({
  pagination,
  pageSizeOptions = DEFAULT_SIZES,
}: Props): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground">
      <label className="flex items-center gap-2">
        <span>{t('dataTable.rows_per_page')}</span>
        <select
          value={pagination.pageSize}
          onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
          className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
          data-testid="datatable-page-size"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <span data-testid="datatable-page-indicator">
          {t('dataTable.page_n', { n: pagination.pageIndex + 1 })}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={pagination.onPrev}
          disabled={!pagination.hasPrev}
          data-testid="datatable-prev"
          aria-label={t('common.previous')}
        >
          <ChevronLeftIcon size={14} />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={pagination.onNext}
          disabled={!pagination.hasNext}
          data-testid="datatable-next"
          aria-label={t('common.next')}
        >
          <ChevronRightIcon size={14} />
        </Button>
      </div>
    </div>
  );
}
